import * as React from "react";
import { List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import type { CSSObject, SxProps, Theme } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { CaretDownIcon } from "@phosphor-icons/react";

import { Dropdown } from "../core/dropdown/dropdown";
import { DropdownContext } from "../core/dropdown/dropdown-context";
import { DropdownPopover } from "../core/dropdown/dropdown-popover";
import { DropdownTrigger } from "../core/dropdown/dropdown-trigger";
import { TableHeadCell } from "./types";

// ----------------------------------------------------------------------

const visuallyHidden: CSSObject = {
	border: 0,
	padding: 0,
	width: "1px",
	height: "1px",
	margin: "-1px",
	overflow: "hidden",
	position: "absolute",
	whiteSpace: "nowrap",
	clip: "rect(0 0 0 0)",
};

export type TableHeadCustomProps = {
	orderBy?: string;
	rowCount?: number;
	sx?: SxProps<Theme>;
	order?: "asc" | "desc";
	headCells: TableHeadCell[];
	onSort?: (name: string) => void;
	onSelectAllRows?: (checked: boolean) => void;
	onDeselectAllRows?: () => void;
	hideSelectAll?: boolean;
	hasExpandableRows?: boolean;
	enableSelectAllMode?: boolean;
	isAllDataSelected?: boolean;
	setIsAllDataSelected?: (checked: boolean) => void;
	totalItems?: number;
	selectedRowCount?: number;
	selectedRows?: string[];
};

export function TableHeadCustom({
	sx,
	order,
	onSort,
	orderBy,
	headCells,
	rowCount = 0,
	onSelectAllRows,
	onDeselectAllRows,
	hideSelectAll = false,
	hasExpandableRows = false,
	enableSelectAllMode = false,
	isAllDataSelected = false,
	setIsAllDataSelected = () => {},
	totalItems = 0,
	selectedRowCount = 0,
	selectedRows = [],
}: TableHeadCustomProps) {
	const hasSelectedRows = selectedRows.length > 0;
	const isIndeterminate = hasSelectedRows && selectedRowCount < rowCount;
	const isChecked = hasSelectedRows && selectedRowCount === rowCount;

	const isEnabledAllChecked = isAllDataSelected ? true : hasSelectedRows && selectedRowCount === rowCount;
	const isEnabledAllIndeterminate = isAllDataSelected ? false : hasSelectedRows && selectedRowCount !== rowCount;

	return (
		<TableHead sx={sx}>
			<TableRow>
				{hasExpandableRows && <TableCell padding="checkbox">{/* Empty cell for expand/collapse column */}</TableCell>}
				{!hideSelectAll && onSelectAllRows && (
					<TableCell padding="checkbox">
						{!enableSelectAllMode && (
							<Checkbox
								indeterminate={isIndeterminate}
								checked={isChecked}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => onSelectAllRows(event.target.checked)}
								slotProps={{
									input: {
										id: `all-row-checkbox`,
										"aria-label": `All row Checkbox`,
									},
								}}
							/>
						)}

						{enableSelectAllMode && (
							<Dropdown>
								<DropdownTrigger>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
										<Checkbox
											indeterminate={isEnabledAllIndeterminate}
											checked={isEnabledAllChecked}
											slotProps={{
												input: {
													id: `all-row-checkbox`,
													"aria-label": `All row Checkbox`,
												},
											}}
										/>
										<CaretDownIcon color="var(--mui-palette-text-primary)" size={16} />
									</Box>
								</DropdownTrigger>
								<DropdownPopover>
									<SelectedRowsDropdownList
										onSelectAllRows={onSelectAllRows}
										onDeselectAllRows={onDeselectAllRows}
										isAllDataSelected={isAllDataSelected}
										setIsAllDataSelected={setIsAllDataSelected}
										totalItems={totalItems}
										selectedCount={selectedRows.length}
										selectedAllOnPage={selectedRowCount === rowCount}
									/>
								</DropdownPopover>
							</Dropdown>
						)}
					</TableCell>
				)}

				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={headCell.align as "left" | "right" | "center" | "inherit" | "justify" | undefined}
						sortDirection={orderBy === headCell.id ? order : false}
						sx={[
							{ width: headCell.width, whiteSpace: "nowrap" },
							...(Array.isArray(headCell.sx) ? headCell.sx : [headCell.sx]),
						]}
					>
						{onSort && headCell.sortBy ? (
							<TableSortLabel
								active={orderBy === headCell.sortBy}
								direction={orderBy === headCell.sortBy ? order : "asc"}
								onClick={() => onSort(headCell?.sortBy ?? "")}
								sx={{
									cursor: "pointer",
									"& .MuiTableSortLabel-icon": {
										opacity: orderBy === headCell.sortBy ? 1 : 0.45,
										transition: "opacity 0.2s",
									},
									"&:hover .MuiTableSortLabel-icon": {
										opacity: 1,
									},
								}}
							>
								{headCell.label}

								{orderBy === headCell.sortBy ? (
									<Box component="span" sx={visuallyHidden}>
										{order === "desc" ? "sorted descending" : "sorted ascending"}
									</Box>
								) : null}
							</TableSortLabel>
						) : (
							headCell.label
						)}
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

function SelectedRowsDropdownList({
	onSelectAllRows,
	isAllDataSelected,
	onDeselectAllRows,
	setIsAllDataSelected,
	totalItems,
	selectedCount,
	selectedAllOnPage,
}: {
	onSelectAllRows: (checked: boolean) => void;
	isAllDataSelected: boolean;
	onDeselectAllRows?: () => void;
	setIsAllDataSelected: (checked: boolean) => void;
	totalItems: number;
	selectedCount: number;
	selectedAllOnPage: boolean;
}) {
	const { onClose } = React.useContext(DropdownContext);

	const actions = [
		{
			label: "Select All On Page",
			onClick: () => {
				onSelectAllRows(true);
			},
			disabled: isAllDataSelected || selectedAllOnPage,
		},
		{
			label: `Deselect All On Page`,
			onClick: () => {
				onSelectAllRows(false);
			},
			disabled: isAllDataSelected,
		},
		{
			label: `Select All (${totalItems})`,
			onClick: () => {
				setIsAllDataSelected(true);
				onSelectAllRows(false);
			},
			disabled: isAllDataSelected || totalItems === selectedCount,
		},
		{
			label: `Deselect All (${totalItems})`,
			onClick: () => {
				setIsAllDataSelected(false);
				onDeselectAllRows?.();
			},
		},
	];

	return (
		<List>
			{actions.map((action) => (
				<ListItem key={action.label} disablePadding>
					<ListItemButton
						disabled={action.disabled}
						onClick={() => {
							action.onClick();
							onClose();
						}}
						dense
						sx={{
							py: 1,
							gap: 1,
							alignItems: "center",
							"& .MuiListItemText-primary": { fontSize: "0.875rem" },
						}}
					>
						<ListItemText
							primary={action.label}
							sx={{
								"& .MuiListItemText-primary": { fontSize: "0.875rem" },
							}}
						/>
					</ListItemButton>
				</ListItem>
			))}
		</List>
	);
}
